import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import { trackEvent } from '../utils/analytics';
import {
  EmailIcon, EmailShareButton,
  FacebookIcon, FacebookShareButton,
  TwitterIcon, TwitterShareButton,
  FacebookMessengerIcon, FacebookMessengerShareButton,
  LinkedinIcon, LinkedinShareButton,
  WhatsappIcon, WhatsappShareButton,
} from 'react-share';
import { Tooltip } from 'react-tooltip';

const STATUS = {
  processing:       { text: 'In Progress',      color: '#d97706', bg: '#fef3c7', description: 'Your custom chatbot is being created. A link will be sent to you once it is ready.' },
  creating_sitemap: { text: 'Creating Sitemap', color: '#d97706', bg: '#fef3c7', description: 'Your sitemap is being created, once completed we will move forward with chatbot creation.' },
  ready:            { text: 'Ready',             color: '#16a34a', bg: '#dcfce7', description: 'You can now talk to your CustomGPT chatbot. This demo chatbot will self destruct in 24 hours.' },
  failed:           { text: 'Failed',            color: '#dc2626', bg: '#fee2e2', description: 'Your Chatbot Creation Failed.' },
};

function addHttps(url) {
  return url.startsWith('http') ? url : 'https://' + url;
}

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.failed;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', fontSize: 12, fontWeight: 600,
      color: s.color, background: s.bg, borderRadius: 0,
      fontFamily: 'var(--ec-font)',
    }}>
      {['processing','creating_sitemap'].includes(status) && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
          <path d="M12 3v3m6.366-.366-2.12 2.12M21 12h-3m.366 6.366-2.12-2.12M12 21v-3m-6.366.366 2.12-2.12M3 12h3m-.366-6.366 2.12 2.12"/>
        </svg>
      )}
      {status === 'ready' && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke={s.color} strokeWidth="1.8" strokeLinecap="square"/>
        </svg>
      )}
      {s.text}
    </span>
  );
};

const StatusChecker = () => {
  const { email } = useParams();
  const [chatbots, setChatbots] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const [share, setShare] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`/chatbots?email=${encodeURIComponent(email)}`);
        const json = await res.json();
        setChatbots(json);
        const hasProcessing = json.some(c => ['processing','creating_sitemap'].includes(c.status));
        if ((!hasProcessing && intervalId) || json.length === 0) clearInterval(intervalId);
      } catch (e) {
        console.error('Error fetching chatbots:', e);
      }
    };

    if (!mounted) {
      if (email) {
        trackEvent('Status Page Viewed', { chatbot_count: 0, has_ready_chatbot: false, has_processing_chatbot: false }, { email, websiteUrl: '' });
      }
      const id = setInterval(fetchData, 10000);
      setIntervalId(id);
      fetchData();
      setMounted(true);
      return () => clearInterval(id);
    }
  }, [email]);

  const trackCTA = (type, text, dest, chatbot) =>
    trackEvent('CTA Clicked', { cta_type: type, cta_text: text, cta_destination: dest, project_id: chatbot?.project_id }, { email, websiteUrl: chatbot?.url || '' });

  const openShare = (url) => {
    const chatbot = chatbots.find(c => c.chatbot_url === url);
    trackEvent('Chatbot Share Clicked', { chatbot_url: url, chatbot_status: chatbot?.status, project_id: chatbot?.project_id, pages_indexed: chatbot?.pages_indexed }, { email, websiteUrl: chatbot?.url || '' });
    setShare(url);
  };

  return (
    <div className="ec-widget w-full max-w-[960px]">
      <div className="flex flex-col items-center justify-center py-8 px-6 sm:px-10 md:px-16 gap-6">
        <div className="ec-panel w-full">

          {/* Panel header */}
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <span style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 16, color: 'var(--ec-text)' }}>
              Your demo custom AIs
            </span>
            <span style={{ fontFamily: 'var(--ec-font)', fontSize: 13, color: 'var(--ec-primary)' }}>{email}</span>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block w-full overflow-y-auto" style={{ maxHeight: 250 }}>
            <table className="ec-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Pages</th>
                  <th>Status</th>
                  <th>Chatbot</th>
                </tr>
              </thead>
              <tbody>
                {chatbots.map((chatbot, i) => (
                  <tr key={i}>
                    <td>
                      <div className="flex items-center gap-1">
                        <a href={chatbot.url} style={{ color: 'var(--ec-text)', textDecoration: 'none', fontWeight: 500 }}>{chatbot.url}</a>
                        <a href={addHttps(chatbot.url)}>
                          <img className="w-4 h-4" alt="" src="/open.svg" />
                        </a>
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {chatbot.sitemap && (
                          <a href={chatbot.sitemap} className="ec-btn-outline" style={{ padding: '4px 10px', fontSize: 11 }}>View Pages</a>
                        )}
                        <a data-tooltip-id="tt-pages" data-tooltip-content={`We ${chatbot.sitemap ? 'selected' : 'are selecting'} 100 random pages from your website to build the demo custom GPT. Please sign up to build a complete custom GPT.`}>
                          <img className="w-4 h-4 ml-1" alt="" src="/infocircle.svg" />
                        </a>
                      </div>
                    </td>
                    <td className="text-center">
                      <StatusBadge status={chatbot.status} />
                    </td>
                    <td className="text-center">
                      {chatbot.chatbot_url ? (
                        <div className="flex items-center justify-center gap-2">
                          <a
                            href={chatbot.chatbot_url}
                            className="ec-cta"
                            style={{ padding: '8px 16px', fontSize: 13, textDecoration: 'none' }}
                            onClick={() => trackCTA('chat_now', 'Chat Now', chatbot.chatbot_url, chatbot)}
                          >
                            Chat Now
                          </a>
                          <button
                            className="ec-btn-outline"
                            style={{ padding: '7px 10px' }}
                            onClick={() => openShare(chatbot.chatbot_url)}
                          >
                            <img className="w-4 h-4" alt="Share" src="/share.svg" />
                          </button>
                        </div>
                      ) : (
                        <a data-tooltip-id="tt-status" data-tooltip-content={STATUS[chatbot.status]?.description}>
                          <img className="w-4 h-4" alt="" src="/infocircle.svg" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-4 md:hidden overflow-y-auto" style={{ maxHeight: 200 }}>
            {chatbots.map((chatbot, i) => (
              <div key={i} className="ec-bot-card">
                <div className="ec-bot-card-row">
                  <span className="ec-label">Domain</span>
                  <div className="flex items-center gap-1 overflow-hidden">
                    <a href={chatbot.url} style={{ color: 'var(--ec-text)', textDecoration: 'none', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chatbot.url}</a>
                    <a href={addHttps(chatbot.url)}><img className="w-4 h-4 flex-shrink-0" alt="" src="/open.svg" /></a>
                  </div>
                </div>
                <div className="ec-bot-card-row">
                  <span className="ec-label">Pages</span>
                  {chatbot.sitemap && (
                    <a href={chatbot.sitemap} className="ec-btn-outline" style={{ padding: '4px 10px', fontSize: 11 }}>View Pages</a>
                  )}
                </div>
                <div className="ec-bot-card-row flex-col items-start gap-2">
                  <div className="flex items-center justify-between w-full">
                    <span className="ec-label">Status</span>
                    <StatusBadge status={chatbot.status} />
                  </div>
                  <p style={{ fontFamily: 'var(--ec-font)', fontSize: 12, color: 'var(--ec-muted)', margin: 0 }}>
                    {STATUS[chatbot.status]?.description}
                  </p>
                </div>
                {chatbot.chatbot_url && (
                  <div className="ec-bot-card-row gap-3">
                    <a
                      href={chatbot.chatbot_url}
                      className="ec-cta flex-1 text-center"
                      style={{ padding: '9px 12px', fontSize: 13, textDecoration: 'none' }}
                      onClick={() => trackCTA('chat_now', 'Chat Now', chatbot.chatbot_url, chatbot)}
                    >
                      Chat Now
                    </a>
                    <button
                      className="ec-btn-outline flex-1 justify-center"
                      onClick={() => openShare(chatbot.chatbot_url)}
                    >
                      <img className="w-4 h-4" alt="" src="/share.svg" />
                      <span>Share</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Sign up section */}
          <div style={{ borderTop: '1.5px solid var(--ec-border)', marginTop: 24, paddingTop: 20 }}>
            <p style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 16, color: 'var(--ec-text)', margin: '0 0 14px' }}>
              Ready to Get Started? Sign Up Now
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <a
                href="https://app.customgpt.ai/register"
                className="ec-cta flex-1 text-center"
                style={{ textDecoration: 'none', padding: '11px 20px' }}
                onClick={() => trackCTA('signup', 'Sign Up Now', 'https://app.customgpt.ai/register', null)}
              >
                Sign Up Now
              </a>
              <a
                href="https://app.customgpt.ai/oauth/google"
                className="ec-btn-outline flex-1 justify-center"
                style={{ padding: '11px 20px' }}
                onClick={() => trackCTA('signup_google', 'Sign Up with Google', 'https://app.customgpt.ai/oauth/google', null)}
              >
                <img className="w-4 h-4" alt="" src="/google.svg" />
                Sign Up with Google
              </a>
            </div>
          </div>
        </div>

        <Tooltip id="tt-pages" className="z-50 !text-xs !max-w-[260px]" />
        <Tooltip id="tt-status" className="z-50 !text-xs !max-w-[260px]" />
      </div>

      {share && (
        <Modal closeConfirmation={() => setShare(null)}>
          <p style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 16, margin: '0 0 16px', textAlign: 'center' }}>Share</p>
          <div className="flex justify-evenly gap-2">
            <FacebookShareButton url={share}><FacebookIcon size={32} round /></FacebookShareButton>
            <FacebookMessengerShareButton url={share}><FacebookMessengerIcon size={32} round /></FacebookMessengerShareButton>
            <TwitterShareButton url={share}><TwitterIcon size={32} round /></TwitterShareButton>
            <WhatsappShareButton url={share}><WhatsappIcon size={32} round /></WhatsappShareButton>
            <EmailShareButton url={share}><EmailIcon size={32} round /></EmailShareButton>
            <LinkedinShareButton url={share}><LinkedinIcon size={32} round /></LinkedinShareButton>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StatusChecker;
